import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {filter, map, switchMap, take} from 'rxjs/operators';
import { StatusOfTask, TaskNotification } from '../models';
import { TaskNotificationsHttpService } from './task-notifications-http.service';
import { AccountSettingsService } from '../../settings/services/account-settings.service';
import {UserAccount} from "../../core/models/account";

@Injectable({
  providedIn: 'root'
})
export class TaskNotificationsService {
  private taskNotifications: TaskNotification[] = [];
  private readonly _taskNotifications: Subject<TaskNotification[]> = new BehaviorSubject<TaskNotification[]>(this.taskNotifications);
  public readonly taskNotifications$: Observable<TaskNotification[]> = this._taskNotifications.asObservable();

  constructor(
    private taskNotificationsHttpService: TaskNotificationsHttpService,
    private accountSettingsService: AccountSettingsService
  ) { }
  
  public setTaskNotifications(taskNotifications: TaskNotification[]): void {
    if (JSON.stringify(taskNotifications) !== JSON.stringify(this.taskNotifications)) {
      this.taskNotifications = taskNotifications;
      this._taskNotifications.next(this.taskNotifications);
      this.sortTaskNotifications();
    }
  }
  
  public updateTaskNotification(taskNotification: TaskNotification): void {
    if (!taskNotification) {
      return;
    }

    const { taskId } = taskNotification;
    this.taskNotificationsHttpService.getTaskById(taskId).pipe(take(1)).subscribe((taskNotification: TaskNotification) => {
      const taskIndex = this.taskNotifications.findIndex((x: TaskNotification) => x.taskId === taskNotification.taskId);
      (taskIndex === -1)
        ? this.taskNotifications.push(taskNotification)
        : this.taskNotifications[taskIndex] = taskNotification;

      this._taskNotifications.next(this.taskNotifications);
      this.sortTaskNotifications();
    });
  }
  
  public filterTaskNotificationsByTaskStatus(taskStatus: StatusOfTask): void {
    const filteredTaskNotifications: TaskNotification[] = this.taskNotifications.filter((taskNotification: TaskNotification) => taskNotification.taskStatus === taskStatus);
    this._taskNotifications.next(filteredTaskNotifications);
  }
  
  public getTaskNotificationsByTaskStatus(taskStatus: StatusOfTask): Observable<TaskNotification[]> {
    return this.taskNotifications$.pipe(
        map((taskNotifications: TaskNotification[]) => taskNotifications.filter((taskNotification: TaskNotification) => taskNotification.taskStatus === taskStatus))
    );
  }
  
  public clearTaskNotifications(): void {
    this.taskNotifications = [];
    this._taskNotifications.next(this.taskNotifications);
  }
  
  public getTodoTaskNotificationsForCurrentUser(): Observable<TaskNotification[]> {
    return this.accountSettingsService.getAccount().pipe(
      switchMap((account: UserAccount) => {
        return this.taskNotifications$.pipe(
          map((taskNotifications: TaskNotification[]) => {
            return  taskNotifications.filter((taskNotification: TaskNotification) => {
              const isTaskNotificationAssignedToCurrentUser: boolean = taskNotification.assignedTo?.toLowerCase() === account.login.toLowerCase();
              const isTaskNotificationNotStarted: boolean = taskNotification.taskStatus === StatusOfTask.NotStarted;
              const isTaskNotificationInProgress: boolean = taskNotification.taskStatus === StatusOfTask.InProgress;
              
              return isTaskNotificationNotStarted || (isTaskNotificationInProgress && isTaskNotificationAssignedToCurrentUser);
            });
          })
        );
      })
    );
  }

  public updateTaskNotificationInList(taskId: number, taskStatus: StatusOfTask): void {
    const taskIndex = this.taskNotifications.findIndex((x: TaskNotification) => x.taskId === taskId);
    if (taskIndex === -1) {
      return;
    }

    this.taskNotifications[taskIndex].taskStatus = taskStatus;
    this._taskNotifications.next(this.taskNotifications);
    this.sortTaskNotifications();
  }
  
  private sortTaskNotifications(): void {
    const sortedTaskNotifications: TaskNotification[] = this.taskNotifications.sort((a: TaskNotification, b: TaskNotification) => a.taskStatus - b.taskStatus);
    this._taskNotifications.next(sortedTaskNotifications);
  }
}
